import { Component, Input, OnChanges } from '@angular/core';
import { GULL_PC_COLORS } from '../line-bird-glyph';
import { PARASOL_PC_COLORS } from '../line-parasol-glyph';
import { SNOW_PC_COLORS } from '../line-snowflake-glyph';
import { JELLY_PC_COLORS, JELLY_PC_OFF } from '../line-jellyfish-glyph';
import { SHELL_PC_COLORS } from '../line-shell-glyph';
import { LAMP_PC_COLORS } from '../line-lamp-glyph';
import { YACHT_PC_COLORS } from '../line-yacht-glyph';
import { BASALT_PC_COLORS } from '../line-basalt-glyph';
import { FLOWER_PC_COLORS } from '../line-meadow-flower-glyph';
import { VIOLIN_PC_COLORS } from '../line-violin-glyph';
import { CLAVEL_PC_COLORS } from '../line-clavel-glyph';
import { VOLCANO_PC_COLORS } from '../line-volcano-glyph';
import { PAGODA_PC_COLORS } from '../line-pagoda-glyph';
import { Product } from '../../../model/product/product.model';

/**
 * The LINE passcode / lock screen as a third preview: a dark screen showing
 * "Enter passcode", the four themed passcode dots (Everest = kawaii mountains
 * lighting up as digits are entered) and the numeric keypad, inside the iPhone
 * frame. Rendered live on the /line workbench and captured for the fanned
 * marketplace screenshot.
 */
@Component( {
  selector: 'app-line-lock',
  templateUrl: './line-lock.component.html',
  styleUrls: [ './line-lock.component.scss' ],
} )
export class LineLockComponent implements OnChanges {

  @Input() product!: Product;

  readonly frameSrc = './assets/common/iphone-17-frame.webp';
  readonly keys = [ '1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del' ];

  background = '';
  // The lock screen tracks the theme's appearance (owner 2026-07-24): light
  // themes get a LIGHT passcode preview (light wallpaper under a light scrim,
  // dark text), dark themes keep the dark backdrop with light text. Set in
  // ngOnChanges from product.appearance.
  bg = '#0C1116';
  fg = '#F2F5F8';
  fgDim = 'rgba(242, 245, 248, 0.6)';
  /** Wallpaper overlay: a light wash on light themes, a heavy dark one on dark. */
  scrim = 'rgba(0, 0, 0, 0.72)';
  /** Two-zone passcode (Sakura): a pink upper block + a pale lower number pad. */
  twoZone = false;
  /** Pink upper-zone colour when two-zone. */
  topZone = '';
  /** Number-pad digit colour when it differs from `fg` (Sakura pink digits). */
  padColor = '';
  /** Passcode variant: Everest = peak, Sakura = blossom, Tokyo = star, else dot. */
  variant: 'dot' | 'mountain' | 'blossom' | 'tower' | 'star' | 'peak' | 'orb' | 'tree' | 'maple' | 'tepui' | 'tumbleweed' | 'sun' | 'palm' | 'gull' | 'eiffel' | 'parasol' | 'snowflake' | 'jellyfish' | 'shell' | 'lamp' | 'yacht' | 'basalt' | 'flower' | 'violin' | 'clavel' | 'volcano' | 'fuji' = 'dot';
  offColor = '#85A2B3';

  /**
   * One ON colour PER dot, in order, from the theme's colour circles
   * (compactPalette 1..4) — e.g. Aurora = blue / green / pink / purple. This
   * matches the passcode assets (i_13/15/17/19), where each entered digit takes
   * a different circle. The 4th slot is empty here (mid-entry), but keeps its
   * colour so a full entry stays consistent.
   */
  dotColors = [ '#549EFF', '#1ECFB6', '#BB719B', '#BAA5FF' ];

  /** on/colour/seed per dot: three entered, one empty (mid-entry lock screen);
   *  seed = digit index, driving the sakura blossom's per-flower variation. */
  dots: { on: boolean; color: string; seed: number }[] = [];

  ngOnChanges(): void {
    const p = this.product;
    if ( !p ) {
      return;
    }
    this.background = p.backgroundImages?.length ? p.backgroundImages[ 0 ].src : '';
    this.variant = p.key === 'everest' ? 'peak'
      : p.key === 'sakura' ? 'blossom'
      : p.key === 'tokyo' ? 'star'
      : p.key === 'aurora-borealis' ? 'orb'
      : p.key === 'sequoia' ? 'tree'
      : p.key === 'autumn' ? 'maple'
      : p.key === 'roraima' ? 'sun'
      : p.key === 'miami' ? 'palm'
      // Istanbul's dots are SEAGULLS (owner 2026-08-14), the same bird the
      // shared line-bird-glyph module flies over Roraima's wallpaper.
      : p.key === 'istanbul' ? 'gull'
      // TODO(paris): Paris has NO variant branch yet, so its dots fall through
      // to the plain 'dot'. The Arc de Triomphe glyph is being authored by
      // another agent right now and will be wired here separately — add
      : p.key === 'paris' ? 'eiffel'
      // Rio's dots are a PLAJ ŞEMSİYESİ (owner 2026-08-14) — its Home tab keeps
      // Cristo Redentor, so this is the second theme here where the two differ.
      : p.key === 'rio-de-janeiro' ? 'parasol'
      // Tallinn's dots are a KAR TANESİ (owner 2026-08-14), the same flake its
      // Home tab and its wallpaper carry.
      : p.key === 'tallinn' ? 'snowflake'
      // Ocean = the jellyfish, the same animal its Home tab and its wallpaper draw.
      : p.key === 'ocean' ? 'jellyfish'
      // Salda = the scallop, the same shell its Home tab and its wallpaper draw.
      : p.key === 'salda' ? 'shell'
      // Prague = the gas street lamp, the same one its Home tab draws.
      : p.key === 'prague' ? 'lamp'
      // Sydney = the Opera House, the same building its Home tab draws.
      : p.key === 'sydney' ? 'yacht'
      // Reynisfjara = the sea stacks, the same rocks its Home tab draws.
      : p.key === 'reynisfjara' ? 'basalt'
      // Spring = the bellflower, the same flower its Home tab draws.
      : p.key === 'spring' ? 'flower'
      // Vienna = the violin, the same instrument its Home tab draws.
      : p.key === 'vienna' ? 'violin'
      // Madrid = the CLAVEL, the same carnation its Home tab draws.
      : p.key === 'madrid' ? 'clavel'
      // Etna = the volcano, the same mountain its Home tab draws.
      : p.key === 'etna' ? 'volcano'
      // Fuji = the mountain itself, the same one its Home tab draws.
      : p.key === 'fuji' ? 'fuji'
      : 'dot';

    // Light themes get a light passcode preview, dark themes the dark backdrop.
    const light = ( p.appearance || '' ).toLowerCase() === 'light';
    this.bg = light ? '#F7EEF2' : '#0C1116';
    this.fg = light ? '#2E2833' : '#F2F5F8';
    this.fgDim = light ? 'rgba(46, 40, 51, 0.55)' : 'rgba(242, 245, 248, 0.6)';
    this.scrim = light ? 'rgba(255, 255, 255, 0.62)' : 'rgba(0, 0, 0, 0.72)';

    // Aurora on the owner's LINE skin (screenshots 2026-08-12): the phone draws
    // the passcode screen in the SKIN's flat near-black, not the theme wallpaper
    // — one zone, white title, dim grey subtitle, white digits. Dropping the
    // wallpaper here is the point: the preview used to promise an aurora photo
    // behind the dots that the device never shows.
    if ( p.key === 'aurora-borealis' ) {
      this.background = '';
      this.bg = '#131313';
      this.fg = '#FFFFFF';
      this.fgDim = '#4F4F4F';
      this.padColor = '#FAFAFA';
      this.scrim = 'transparent';
    }
    // Ocean on its SAMPLED skin: the two-zone screen every light skin in this
    // family takes — the bright teal block on top holding the title and the four
    // jellies, the near-white number pad below it.
    //
    // It shipped INVERTED (deep water on top, lighter pad below) on the reasoning
    // that four bioluminescent creatures need the deepest thing on the screen
    // behind them. The phone draws the teal, so that reasoning is gone and the
    // animals went with it: they are dark creatures on lit water now, and the
    // four-to-one gap is bought by making the UNENTERED one pale instead of the
    // entered ones bright (see JELLY_PC_COLORS).
    if ( p.key === 'ocean' ) {
      // The device never draws the theme's wallpaper photo here, so it is cleared
      // and the scrim goes with it — the same two lines every branch in this
      // method carries. Missing them is what put a blurred seabed photograph
      // behind Ocean's keypad on the first pass, which no other theme here shows.
      this.background = '';
      this.scrim = 'transparent';
      this.twoZone = true;
      this.topZone = '#2FB8A7';   // the lit upper zone the four jellies float in (sampled)
      this.bg = '#EDFAF9';        // the keypad, near-white (sampled)
      this.fg = '#FFFFFF';        // title: white on the teal, what the device draws
      this.fgDim = '#C8EFE8';     // the subtitle, a quiet mint on the same teal
      this.padColor = '#2FB8A7';  // digits: the skin's teal on the pale pad (sampled)
    }
    // Salda on its own derived skin: the two-zone screen the LIGHT skins in this
    // family all take — a pale sand block holding the title and the shells, over
    // a keypad in the lake's own turquoise with near-white digits.
    //
    // The right way round, unlike Ocean's: the dots here are DARK marks, so they
    // want the palest thing on the screen behind them and the colour goes below.
    if ( p.key === 'salda' ) {
      this.background = '';
      this.scrim = 'transparent';
      this.twoZone = true;
      // SAMPLED, and it flips back. This zone pair was inverted deliberately —
      // deep lake on top, sand below — because pale shells needed something dark
      // behind them, and the reasoning was sound for the shells we had. The phone
      // draws the ordinary light-skin arrangement: CREAM on top holding the title
      // and the dots, the lake's turquoise as the keypad below.
      //
      // So the argument that justified the flip is gone, and the shells had to
      // move rather than the zones: they are dark marks on cream now (see
      // SHELL_PC_COLORS), which is what a shell on a beach actually looks like.
      this.topZone = '#FFF8E8';   // cream upper zone, holding title + shells (sampled)
      this.bg = '#7ACACE';        // the keypad, the lake's turquoise (sampled)
      this.fg = '#5D3030';        // title: the warm dark brown the shots draw
      this.fgDim = '#B49A8E';     // subtitle, quiet on the cream
      this.padColor = '#635555';  // digits: warm dark grey on the turquoise (sampled)
    }
    // Prague on its SAMPLED skin: ONE zone, not two. The derived screen split the
    // night into a deeper block for the lamps over a lighter keypad, which is the
    // shape most dark skins here take. This one does not — the phone paints the
    // whole passcode screen in the same near-black, exactly as Aurora's does, so
    // the lamps hang in an unbroken night and the keypad is simply the same wall
    // further down.
    //
    // The lamps needed nothing. They were built to light up against a dark zone
    // and they still do: 4.29 / 5.61 / 6.42 / 11.48 on the sampled black against
    // the unentered one's 1.84. This is the first theme in the run whose sample
    // left its assets alone.
    if ( p.key === 'prague' ) {
      this.background = '';
      this.scrim = 'transparent';
      this.twoZone = false;
      this.topZone = '';
      this.bg = '#131313';        // the whole screen, one near-black (sampled)
      this.fg = '#FFFFFF';        // title: white on the night
      this.fgDim = '#4F4F4F';     // subtitle, quiet — Aurora's own dim grey
      this.padColor = '#FAFAFA';  // digits: near-white (sampled)
    }
    // Sydney on its own derived skin: the two-zone screen the LIGHT skins take —
    // a pale harbour block holding the title and the buildings, over a keypad in
    // the harbour blue with white digits.
    if ( p.key === 'sydney' ) {
      // SAMPLED, and this is the one derived passcode in the run that came out
      // RIGHT: white upper zone holding the title and the boats, the header's blue
      // as the keypad below it. Ocean's and Salda's both had to be inverted when
      // their screenshots arrived, so it is worth recording that the shape here
      // needed nothing — only the exact hexes moved.
      this.background = '';
      this.scrim = 'transparent';
      this.twoZone = true;
      this.topZone = '#FFFFFF';   // white upper zone, title + boats (sampled)
      this.bg = '#0D8DC3';        // the keypad, the harbour blue (sampled)
      this.fg = '#1C1C1C';        // title: near-black on the white
      this.fgDim = '#8A8A8A';     // subtitle, neutral grey
      this.padColor = '#FFFFFF';  // digits: white on the blue (sampled)
    }

    // Reynisfjara on its own derived skin: the two-zone screen, inverted like
    // Ocean's and Prague's — the block holding the stacks is the DEEPER dark and
    // the keypad below it is the lighter grey. The dots light UP, so they want the
    // darkest thing on the screen behind them.
    if ( p.key === 'reynisfjara' ) {
      // SAMPLED: ONE zone, not two. The derived screen split the dark into a
      // deeper block for the columns over a lighter keypad; this skin paints the
      // whole passcode screen in the same near-black, exactly as Aurora's and
      // Prague's do.
      //
      // The clusters needed nothing. They were built to light up against a dark
      // zone and they still do — 2.29 / 3.48 / 4.18 / 5.92 against the unentered
      // one's 1.06.
      this.background = '';
      this.scrim = 'transparent';
      this.twoZone = false;
      this.topZone = '';
      this.bg = '#131313';        // the whole screen, one near-black (sampled)
      this.fg = '#FFFFFF';
      this.fgDim = '#4F4F4F';
      this.padColor = '#FAFAFA';
    }

    // Spring on its own derived skin: the two-zone screen the LIGHT skins take —
    // a pale meadow block holding the title and the birds, over a keypad in the
    // meadow green with white digits.
    if ( p.key === 'spring' ) {
      // SAMPLED, and the SHAPE was already right: white upper zone holding the
      // title and the flowers, the skin's green as the keypad below it. Only the
      // exact hexes moved — the second derived passcode in the run to come out
      // right, after Sydney's.
      this.background = '';
      this.scrim = 'transparent';
      this.twoZone = true;
      this.topZone = '#FFFFFF';   // white upper zone, title + bells (sampled)
      this.bg = '#BCDA85';        // the keypad, the skin's fresh green (sampled)
      this.fg = '#2B2B2B';
      this.fgDim = '#9AA69B';
      this.padColor = '#FFFFFF';  // white digits on the green (sampled)
    }

    // Vienna on its own derived skin: the two-zone screen, inverted like every
    // dark theme here — the block holding the violins is the DEEPER wine and the
    // keypad below it is the lighter one. The dots light UP, so they want the
    // darkest thing on the screen behind them.
    // Vienna on its SAMPLED skin: the two-zone screen, and both zones came
    // straight off the owner's own lock-screen shot — a blush field above, LINE's
    // vermilion keypad below, white digits. The wine-on-wine version this replaced
    // put the pale zone under the dark one, which is the wrong way up for a keypad.
    if ( p.key === 'vienna' ) {
      this.background = '';
      this.scrim = 'transparent';
      this.twoZone = true;
      this.topZone = '#F8EDE7';   // the blush the four violins lie on
      this.bg = '#EE4333';        // the keypad: LINE's vermilion
      this.fg = '#3A2422';        // title: the warm near-black, 9.9:1 on the blush
      this.fgDim = '#8A7168';     // subtitle, quiet
      this.padColor = '#FFFFFF';  // digits: white, 3.83:1 on the vermilion
    }
    // Madrid on its own derived skin: the two-zone screen the LIGHT skins take —
    // a warm cream block holding the title and the fans, over a keypad in the
    // flamenco red with white digits.
    // Madrid on its SAMPLED skin: both zones came off the owner's own lock-screen
    // shot. The keypad is NOT red — it is the same saffron as everything else on
    // this skin — and its digits are the dark warm ink, because white on #FFDD66
    // measures 1.33 and disappears.
    if ( p.key === 'madrid' ) {
      this.background = '';
      this.scrim = 'transparent';
      this.twoZone = true;
      this.topZone = '#FEF9E8';   // the cream upper zone the four cups lie on
      this.bg = '#FFDD66';        // the keypad: the skin's one saffron
      this.fg = '#3A2F08';        // title: the warm near-black, 10.7:1 on the cream
      this.fgDim = '#A08F5E';     // subtitle, quiet
      this.padColor = '#3A2F08';  // digits: the same ink, 9.93:1 on the saffron
    }
    // Etna on its SAMPLED skin: ONE zone, not two.
    //
    // The derived version split the screen — a deeper black holding the volcanoes
    // over a lighter charcoal keypad — and the owner's own shot has no split in it
    // at all: the whole lock screen is a single #131313 with white digits on it.
    // That is what LINE's stock near-black does, and it is the same single-zone
    // screen Aurora, Prague and Reynisfjara take.
    //
    // The ember-sand digits went with the split. On this skin the only warm thing
    // anywhere is a volcano.
    if ( p.key === 'etna' ) {
      this.background = '';
      this.scrim = 'transparent';
      this.twoZone = false;
      this.bg = '#131313';        // the whole screen, keypad included
      this.fg = '#FFFFFF';        // title
      this.fgDim = '#6E6666';     // subtitle, quiet
      this.padColor = '#FFFFFF';  // digits: white, as the sample shows
    }
    // Fuji on its SAMPLED skin: the two-zone screen, and both zones came straight
    // off the owner's own lock-screen shot — the blush above, LINE's coral keypad
    // below, white digits on it at 2.13:1, which is the phone's own choice rather
    // than one this file gets to make.
    if ( p.key === 'fuji' ) {
      this.background = '';
      this.scrim = 'transparent';
      this.twoZone = true;
      this.topZone = '#FFE8E1';   // the blush the four mountains stand on
      this.bg = '#FE939C';        // the keypad: LINE's coral
      this.fg = '#8C5358';        // title: the rose-brown ink, 5.12:1 on the blush
      this.fgDim = '#C4A79E';     // subtitle, quiet
      this.padColor = '#FFFFFF';  // digits: white, as the sample shows
    }
    // Sequoia on the owner's GREEN skin (screenshots 2026-08-13): a TWO-ZONE
    // screen like Sakura's — a pale cream upper block holding the trees, over a
    // green number pad with white digits. Not the theme wallpaper, which the
    // phone never draws here.
    if ( p.key === 'sequoia' ) {
      this.background = '';
      this.twoZone = true;
      this.topZone = '#EFEFD9';   // warm cream upper zone
      this.bg = '#2BAB5E';        // the keypad's green
      this.fg = '#18723B';        // deep green title on the cream
      this.fgDim = '#94BD99';
      this.padColor = '#FFFFFF';  // white digits on the green pad
      this.scrim = 'transparent';
    }
    // Autumn on the owner's chosen skin (screenshots 2026-08-13): the two-zone
    // screen again — a cream upper block holding the maples, over a CARAMEL
    // number pad with cream digits. Every hex pixel-sampled; the provisional
    // rust this replaces was a shade too red and too dark.
    if ( p.key === 'autumn' ) {
      this.background = '';
      this.twoZone = true;
      this.topZone = '#FFF1DD';   // cream upper zone (sampled)
      this.bg = '#C7813B';        // the keypad's caramel (sampled)
      this.fg = '#C7813C';        // title: the same caramel, on the cream
      this.fgDim = '#E9C69B';     // subtitle: pale tan (sampled)
      this.padColor = '#FFF1DD';  // digits: the cream, not pure white (sampled)
      this.scrim = 'transparent';
    }
    // Roraima on the owner's dark-brown skin (screenshots 2026-08-13): the
    // two-zone screen, but INVERTED against every light skin in this family —
    // the upper block holding the dots is the DARKER brown and the number pad
    // below it is the lighter one. White title, white digits. The provisional
    // pass had it exactly the other way round, sand over terracotta.
    if ( p.key === 'roraima' ) {
      this.background = '';
      this.twoZone = true;
      this.topZone = '#422C21';   // dark upper zone (sampled)
      this.bg = '#5C3E30';        // the keypad, LIGHTER than the zone above it
      this.fg = '#FFFFFF';        // title: white on the dark zone
      this.fgDim = '#99837B';     // subtitle: muted warm grey
      this.padColor = '#FCFCFC';  // white digits
      this.scrim = 'transparent';
    }
    // Miami on the owner's hot-pink skin (screenshots 2026-08-13): the two-zone
    // screen, pink upper block over a pale pink keypad whose digits are pink too
    // — the first skin here whose number pad is not white, grey or cream.
    if ( p.key === 'miami' ) {
      this.background = '';
      this.twoZone = true;
      this.topZone = '#FC5B96';   // hot pink upper zone
      this.bg = '#FFECF3';        // the keypad's palest pink
      this.fg = '#FFFFFF';        // title: white on the pink
      this.fgDim = '#FDA2C4';     // subtitle: a lighter pink
      this.padColor = '#FC5E98';  // pink digits
      this.scrim = 'transparent';
    }
    // Istanbul on the owner's cyan skin (screenshots 2026-08-14): the two-zone
    // screen once more — a bright cyan upper block holding the title and the
    // dots, over a near-white keypad with mid-cyan digits. Every hex is
    // pixel-sampled from the device, not derived from the theme's designTokens.
    if ( p.key === 'istanbul' ) {
      this.background = '';
      this.twoZone = true;
      this.topZone = '#4AC3E0';   // bright cyan upper zone (the skin's surface)
      this.bg = '#F9FEFF';        // the keypad, near-white
      this.fg = '#FFFFFF';        // title: white on the cyan
      this.fgDim = 'rgba(255, 255, 255, 0.82)';  // subtitle: softer white
      this.padColor = '#2EAACB';  // digits: the mid cyan of the input-bar pill
      this.scrim = 'transparent';
    }
    // Paris: PLACEHOLDER — every hex here is RORAIMA's, copied verbatim, so the
    // passcode preview inherits Roraima's inverted two-zone treatment (the block
    // holding the dots is the DARKER brown, the number pad below it the lighter
    // one). The owner has not picked Paris's LINE colour skin yet and will set
    // Paris, sampled from the device (2026-08-14), and it is the family's first
    // INVERTED passcode. Every other theme here puts the colour on top and the
    // keypad below it: Sakura's pink block over a pale pad, Istanbul's cyan block
    // over a near-white one. Paris does the reverse — a PALE rose upper zone
    // holding the title and the dots, over a keypad in the skin's hot pink with
    // white digits.
    //
    // That inversion decides the dots. They sit on the pale zone now, so the
    // passcode glyph has to be DARK, where on every previous theme it was light.
    if ( p.key === 'paris' ) {
      this.background = '';
      this.twoZone = true;
      this.topZone = '#FFDADA';   // pale rose upper zone, holding title + dots
      this.bg = '#FE7897';        // the keypad below it, the skin's hot pink
      this.fg = '#8C4257';        // title: a deep rose on the pale zone
      this.fgDim = 'rgba(140, 66, 87, 0.62)';
      this.padColor = '#FFFFFF';  // digits: white on the pink pad
      this.scrim = 'transparent';
    }
    // Tallinn on the owner's chosen skin (screenshots 2026-08-14 22.06): the
    // two-zone screen with the WHITE half on top — the title and the dots sit on
    // white, the azure keypad is below them with white digits.
    //
    // That decides the dots. They are on WHITE, so Tallinn's snowflakes are DARK
    // blues, not the ice-whites the borrowed cyan skin had allowed.
    if ( p.key === 'tallinn' ) {
      this.background = '';
      this.twoZone = true;
      this.topZone = '#FFFFFF';   // white upper zone (sampled)
      this.bg = '#0D8DC3';        // the keypad's azure (sampled)
      this.fg = '#0D8DC3';        // title: the azure, on the white
      this.fgDim = '#B2DFEE';     // subtitle: pale blue (sampled)
      this.padColor = '#FFFFFF';  // white digits on the azure pad
      this.scrim = 'transparent';
    }
    // Rio on the owner's second skin (screenshots 2026-08-14 21.23): the two-zone
    // screen in its starkest form — a WHITE upper block holding the title and the
    // dots, over a green number pad with white digits. No tint anywhere in the
    // top half.
    //
    // That decides the dots. They sit on WHITE, so Rio's statues are DARK — the
    // same call Paris's inverted screen forced, and the opposite of what the
    // provisional emerald zone had allowed.
    if ( p.key === 'rio-de-janeiro' ) {
      this.background = '';
      this.twoZone = true;
      this.topZone = '#FFFFFF';   // white upper zone (sampled)
      this.bg = '#00A64B';        // the keypad's green (the owner's own value)
      this.fg = '#121212';        // near-black title on the white
      this.fgDim = '#B7B7B7';
      this.padColor = '#FFFFFF';  // white digits on the green pad
      this.scrim = 'transparent';
    }
    // Sakura (owner 2026-07-29 passcode reference): a TWO-ZONE screen — a pink
    // upper block (title + blossom dots, white text) over a pale-pink lower
    // number pad (pink digits). All hexes pixel-sampled from the mockup.
    if ( p.key === 'sakura' ) {
      this.background = '';
      this.twoZone = true;
      this.bg = '#FFEEF2';        // pale lower zone (screen + pad)
      this.topZone = '#FEA5B9';   // pink upper zone
      this.fg = '#FFFFFF';        // title white
      this.fgDim = 'rgba(255, 255, 255, 0.82)';
      this.padColor = '#FFA2B8';  // pink number pad
      this.scrim = 'transparent';
    }
    // Everest on the owner's LINE skin (screenshots 2026-08-12): the two-zone
    // screen inverts against Sakura's — a WHITE upper block (blue title + the
    // summits) over a DEEP BLUE number pad with white digits.
    if ( p.key === 'everest' ) {
      this.background = '';
      this.twoZone = true;
      this.bg = '#1469B2';        // deep blue lower zone (the number pad)
      this.topZone = '#FFFFFF';   // white upper zone
      this.fg = '#186DB4';        // title in the skin's blue on the white
      this.fgDim = '#B1D0E7';     // subtitle: pale blue
      this.padColor = '#FFFFFF';  // white digits on the blue pad
      this.scrim = 'transparent';
    }
    // Tokyo (owner mockup): the same two-zone screen, inverted — a LAVENDER upper
    // block (dark-plum title + dots) over a DARK-PLUM number pad (light-lavender
    // digits). All hexes pixel-sampled from the passcode mockup.
    if ( p.key === 'tokyo' ) {
      this.background = '';
      this.twoZone = true;
      this.topZone = '#B99EC5';   // lavender upper zone
      this.bg = '#51385E';        // dark-plum lower zone (keypad)
      this.fg = '#51385E';        // dark-plum title on the lavender
      this.fgDim = 'rgba(81, 56, 94, 0.62)';
      this.padColor = '#D5CED9';  // light-lavender digits on the plum pad
      this.scrim = 'transparent';
    }

    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    const t: any = p.designTokens || {};
    this.offColor = t.secondaryForeground || '#85A2B3';
    // Colour circles 2..5 (compactPalette 1..4) in order — circle 1 is the dark
    // bg, skipped — matching passcode-dots() and the uploaded assets.
    this.dotColors = [
      cp[ 1 ] || t.primaryColor || '#549EFF',
      cp[ 2 ] || t.secondaryColor || '#1ECFB6',
      cp[ 3 ] || t.tertiaryColor || '#BB719B',
      cp[ 4 ] || '#BAA5FF',
    ];
    // Tokyo star dots (owner 2026-08-05, tower retired): light-yellow twinkles
    // that glow warmly on the lavender zone — a few pale-gold tints for variety.
    if ( p.key === 'tokyo' ) {
      this.dotColors = [ '#FFF0BE', '#FFF3CE', '#FFEAA6', '#FFE49C' ];
    }
    // Miami's palms are all ONE neon green (owner 2026-08-13): Electric Aqua, the
    // green of the theme's COLOUR CIRCLE. Kept in step with
    // line-page.passcodeDots, which is what the exported PNGs come from — those
    // two drifting apart is exactly how Everest ended up with summits in its
    // screenshot and dawn circles in its shipped assets.
    if ( p.key === 'miami' ) {
      this.dotColors = [ '#00D1C1', '#00D1C1', '#00D1C1', '#00D1C1' ];
    }
    // Istanbul's gulls are one colour for all four entered dots, not one per
    // digit (line-bird-glyph carries the tone and the measurement). Kept in
    // step with line-page.passcodeDots by importing the one array both read —
    // this is the pair the comment above warns about.
    if ( p.key === 'istanbul' ) {
      this.dotColors = GULL_PC_COLORS.slice();
    }
    // Rio's four parasols are identical — the two-colour pair is the design, so
    // there is no per-digit tint. Read from the same constant
    // line-page.passcodeDots uses, so preview and exported PNG cannot drift.
    if ( p.key === 'rio-de-janeiro' ) {
      this.dotColors = PARASOL_PC_COLORS.slice();
    }
    // Tallinn's four flakes are one drawing in four ice tints. Read from the same
    // constant line-page.passcodeDots uses, so preview and exported PNG cannot
    // drift.
    if ( p.key === 'tallinn' ) {
      this.dotColors = SNOW_PC_COLORS.slice();
    }
    // Ocean's four jellies, and its UNENTERED tone with them. This branch was
    // missing, and the gap is the one the Miami comment above warns about: with no
    // per-key entry the preview fell through to colour circles 2..5 and drew four
    // marketing swatches, while the exported PNGs drew JELLY_PC_COLORS. The owner
    // was reviewing a lock screen no phone would ever show ("previewlar assetleri
    // reflect edecek", 2026-08-15).
    //
    // offColor matters as much as the four here and nowhere else does it: every
    // other theme's generic secondaryForeground happens to be a quiet tone on its
    // own zone, and Ocean's (#88AAB7) is a blue-grey that outweighs all four lit
    // jellies on the teal.
    if ( p.key === 'ocean' ) {
      this.dotColors = JELLY_PC_COLORS.slice();
      this.offColor = JELLY_PC_OFF;
    }
    // The other nine, same fix as Ocean's and found with it: every one of these
    // has a glyph module holding its four entered tones, line-page reads that
    // module for the exported PNG, and line-lock had no entry — so nine lock
    // previews were drawing marketing swatches over assets that draw shells,
    // lamps, yachts, puffins, tulips, violins, dancers, craters and pagodas.
    const PINNED: { [ key: string ]: string[] } = {
      'salda': SHELL_PC_COLORS,
      'prague': LAMP_PC_COLORS,
      'sydney': YACHT_PC_COLORS,
      'reynisfjara': BASALT_PC_COLORS,
      'spring': FLOWER_PC_COLORS,
      'vienna': VIOLIN_PC_COLORS,
      'madrid': CLAVEL_PC_COLORS,
      'etna': VOLCANO_PC_COLORS,
      'fuji': PAGODA_PC_COLORS,
    };
    if ( PINNED[ p.key ] ) {
      this.dotColors = PINNED[ p.key ].slice();
    }
    // Three entered, the fourth empty.
    this.dots = this.dotColors.map( ( color, i ) => ( { on: i < 3, color, seed: i } ) );
  }
}
